<template>
    <mercur-card class="full-height-layout fill mx-4 mb-3">
        <div class="row" v-if="!isLoading && !isStockApiEnabled">
            <div class="col col-12 pl-15 pr-15">
                <alert type="danger">
                    This supplier is not yet enabled for the Stock API.
                </alert>
            </div>
        </div>

        <grid-header :quickSearch.sync="filters.search">
            <template slot="smallHeader">
                Stock level configurations
            </template>
            <template slot="actions">
                <mercur-button :to="addRoute" class="btn btn-raised btn-yellow text-uppercase mt-2">
                    <i class="fas fa-plus"></i>
                    <span>Add new configuration</span>
                </mercur-button>
            </template>
        </grid-header>

        <template v-if="url">
            <data-table
                class="fill full-height-layout border"
                :options="options"
                :url="url"
                :quickSearch="filters.search"
                ref="table"
            />
        </template>
        <div>
            <mercur-dialog :is-open.sync="dialog.isOpen" width="600px">
                <p>{{dialog.content}}</p>
                <template slot="footer">
                    <mercur-button class="btn" @click="dialog.isOpen = false">Cancel</mercur-button>
                    <mercur-button class="btn btn-raised btn-primary" @click="dialog.action">Confirm</mercur-button>
                </template>
            </mercur-dialog>
        </div>
    </mercur-card>
</template>
<script>

import CONFIG from '@root/config'
import DataTable from '@/components/DataTable'
import GridHeader from '@/components/GridHeader.vue'
import Alert from '@/components/Alert.vue'

export default {
    name: 'StockLevelConfigurationsOverview',
    components: { GridHeader, DataTable, Alert },
    data () {
        return {
            dialog: {
                isOpen: false,
                action: () => {},
            },
            isLoading: false,
            isStockApiEnabled: false,
            options: {
                columns: {
                    'Product configuration': {
                        field: 'productConfigurationName',
                        sortable: true,
                        link: (value, data) => {
                            return {
                                name: 'SupplierEditStockLevelConfiguration',
                                params: {
                                    ...this.$route.params,
                                    stockLevelConfigurationId: data.supplierStockLevelConfigurationId,
                                },
                            }
                        },
                    },
                    'Template': {
                        field: 'productConfigurationTemplateName',
                        sortable: true,
                    },
                    'Threshold': {
                        field: 'threshold',
                        sortable: true,
                        width: 125,
                    },
                    'Active': {
                        field: 'active',
                        sortable: true,
                        valueFormatter: ({ value }) => value ? 'Yes' : 'No',
                        statusChip: true,
                        width: 100,
                    },
                    'Updated': {
                        field: 'dateUpdated',
                        sortable: true,
                    },
                },
                actions: [
                    {
                        text: 'List stocks',
                        icon: 'fas fa-list-ul small-icon',
                        to: (params) => {
                            return {
                                name: 'SupplierListStockLevelConfigurationStockLevels',
                                params: {
                                    ...this.$route.params,
                                    stockLevelConfigurationId: params.data.supplierStockLevelConfigurationId,
                                },
                            }
                        },
                        disabled: () => this.isLoading,
                    },
                    {
                        text: 'Update stocks',
                        icon: 'fa fa-rotate-right small-icon',
                        onClick: ({ data }) => this.triggerUpdateStockLevels(data),
                        hide: ({ data }) => !data.active,
                        disabled: () => this.isLoading,
                    },
                    {
                        text: 'Clear stocks',
                        icon: 'fas fa-trash small-icon',
                        onClick: ({ data }) => this.triggerClearStockLevels(data),
                        disabled: () => this.isLoading,
                    },
                    {
                        text: 'Enable',
                        icon: 'fas fa-plus small-icon',
                        onClick: ({ data }) => this.triggerToggle(data, 'enable'),
                        hide: ({ data }) => data.active,
                        disabled: () => this.isLoading,
                    },
                    {
                        text: 'Disable',
                        icon: 'fas fa-times small-icon',
                        onClick: ({ data }) => this.triggerToggle(data, 'disable'),
                        hide: ({ data }) => !data.active,
                        disabled: () => this.isLoading,
                    },
                    {
                        text: 'Delete',
                        icon: 'fas fa-trash small-icon',
                        class: 'text-danger',
                        onClick: ({ data }) => this.triggerDelete(data),
                        disabled: () => this.isLoading,
                    },
                ],
                quickSearchColumns: ['productConfigurationName'],
                sortModel: [
                    {
                        colId: 'dateUpdated',
                        sort: 'desc',
                    },
                ],
            },
            filters: {
                search: '',
            },
        }
    },
    computed: {
        url () {
            return CONFIG.API.ROUTES.SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.OVERVIEW.replace('{supplierId}', this.supplierId)
        },
        addRoute () {
            return {
                name: 'SupplierAddStockLevelConfiguration',
                params: {
                    ...this.$route.params,
                },
            }
        },
    },
    methods: {
        triggerDelete (data) {
            this.dialog = {
                isOpen: true,
                content: 'Are you sure that you want to delete this configuration?',
                action: () => this.deleteStockLevelConfiguration(data.supplierStockLevelConfigurationId),
            }
        },
        triggerToggle (data, action) {
            this.dialog = {
                isOpen: true,
                content: `Are you sure that you want to ${action} this configuration?`,
                action: () => this.toggleStockLevelConfiguration(data.supplierStockLevelConfigurationId),
            }
        },
        triggerClearStockLevels (data) {
            this.dialog = {
                isOpen: true,
                content: `Are you sure that you want to clear the stock levels for this configuration?`,
                action: () => this.clearStockLevels(data.supplierStockLevelConfigurationId),
            }
        },
        triggerUpdateStockLevels (data) {
            this.dialog = {
                isOpen: true,
                content: `Are you sure that you want to update the stock levels for this configuration?`,
                action: () => this.updateStockLevels(data.supplierStockLevelConfigurationId),
            }
        },
        deleteStockLevelConfiguration (id) {
            this.doPost(
                CONFIG.API.ROUTES.SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.DELETE.replace('{supplierId}', this.supplierId).replace('{stockLevelConfigurationId}', id),
                {},
                'Stock level configuration was deleted',
                'Deletion failed. Please try again.'
            )
        },
        toggleStockLevelConfiguration (id) {
            this.doPost(
                CONFIG.API.ROUTES.SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.TOGGLE.replace('{supplierId}', this.supplierId).replace('{stockLevelConfigurationId}', id),
                {},
                'Stock level configuration was updated',
                'Update failed. Please try again.'
            )
        },
        clearStockLevels (id) {
            this.doPost(
                CONFIG.API.ROUTES.SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.CLEAR_STOCK_LEVELS.replace('{supplierId}', this.supplierId).replace('{stockLevelConfigurationId}', id),
                {},
                'Stock level configuration was cleared',
                'Action failed. Please try again.'
            )
        },
        updateStockLevels (id) {
            this.doPost(
                CONFIG.API.ROUTES.SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.UPDATE_STOCK_LEVELS.replace('{supplierId}', this.supplierId).replace('{stockLevelConfigurationId}', id),
                {},
                'Stock level update was queued',
                'Action failed. Please try again.'
            )
        },
        doPost (url, data = {}, successMessage, errorMessage) {
            this.isLoading = true
            this.addJob(url)

            this.$api.post(url, data).then(({ data }) => {
                this.$refs.table.refreshGrid()
                this.$root.$emit('notification:global', {
                    message: successMessage,
                })
            }).catch(data => {
                this.$root.$emit('notification:global', {
                    message: errorMessage,
                    type: 'error',
                    errors: data,
                })
            }).finally(() => {
                this.isLoading = false
                this.dialog.isOpen = false
                this.finishJob(url)
            })
        },
    },
    created () {
        this.supplierData.then(supplierDetails => {
            this.isStockApiEnabled = supplierDetails.stockApiEnabled
        })
    },
}
</script>
